import React, { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '../../api/client';

/** Devices signed in to this account (SRS #95): see, sign out one, sign out everywhere else. */

interface SessionRow { session_id: string; ip: string | null; device: string | null; user_agent: string | null; created_at: number; last_seen_at: number; current: boolean }

const opts = (extra: RequestInit = {}): RequestInit => {
  const token = localStorage.getItem('policyora_token');
  return { ...extra, headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } };
};
const when = (ts: number) => (ts ? new Date(ts * 1000).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const ActiveSessions: React.FC = () => {
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    apiRequest<{ sessions: SessionRow[] }>('/api/v1/auth/sessions', opts())
      .then((r) => { setRows(r.sessions); setLoaded(true); })
      .catch((e) => { setError(e instanceof Error ? e.message : 'Could not load sessions'); setLoaded(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key); setError(null);
    try { await fn(); load(); } catch (e) { setError(e instanceof Error ? e.message : 'Action failed'); } finally { setBusy(null); }
  };

  const text = 'var(--color-text, var(--ds-text))';
  const muted = 'var(--color-text-muted, var(--ds-text-muted))';
  const border = 'var(--ds-border, var(--ds-border))';
  const btn: React.CSSProperties = { padding: '5px 10px', borderRadius: 8, border: `1px solid ${border}`, background: 'transparent', cursor: 'pointer', fontSize: 12.5, color: text };

  return (
    <div style={{ marginTop: 18, borderTop: `1px solid ${border}`, paddingTop: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 600, color: text, fontSize: 14 }}>Signed-in devices</div>
          <div style={{ fontSize: 12, color: muted }}>Where this account is currently signed in. Sign out anything you don't recognise.</div>
        </div>
        {rows.length > 1 && (
          <button style={btn} disabled={busy === 'others'} onClick={() => act('others', () => apiRequest('/api/v1/auth/sessions/revoke-others', opts({ method: 'POST' })))}>
            {busy === 'others' ? 'Signing out…' : 'Sign out everywhere else'}
          </button>
        )}
      </div>
      {error && <p style={{ color: 'var(--ds-danger-text)', fontSize: 12.5, margin: '8px 0 0' }}>{error}</p>}
      {/* With a single device there is nothing to sign out, so no button renders.
          Say that, rather than leaving a list with no action on it: read in
          silence it looks as though the feature is missing, which is how it was
          reported (SRS #95). */}
      {loaded && !error && rows.length === 1 && (
        <p data-testid="only-device" style={{ fontSize: 12.5, color: muted, margin: '8px 0 0' }}>
          This is the only device signed in, so there is nothing to sign out. Any other device
          that signs in appears here with its own Sign out button.
        </p>
      )}
      <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((s) => (
          <li key={s.session_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: s.current ? 'rgba(68,76,231,0.08)' : 'var(--ds-surface-2, var(--ds-surface-2))', fontSize: 13 }}>
            <div>
              <div style={{ color: text, fontWeight: 600 }}>{s.device || 'Unknown device'}{s.current && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'var(--ds-link)' }}>this device</span>}</div>
              <div style={{ color: muted, fontSize: 12 }} title={s.user_agent || ''}>{s.ip || 'IP not recorded'} · signed in {when(s.created_at)} · last active {when(s.last_seen_at)}</div>
            </div>
            {!s.current && <button style={btn} disabled={busy === s.session_id} onClick={() => act(s.session_id, () => apiRequest(`/api/v1/auth/sessions/${s.session_id}`, opts({ method: 'DELETE' })))}>Sign out</button>}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ActiveSessions;
